import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSoftwarePool, getSoftwarePool, updateSoftwarePool } from '@/api/software';
import { ApiError } from '@/api/client';
import { softwareSchema, licenceTypes, type SoftwareFormData } from './schema';
import type { SoftwarePoolWrite } from '@/types/oscar';

const empty: SoftwareFormData = { Title: '', LicenceType: 'User', SKU: '', SkuId: null, SecurityGroupId: '', SecurityGroupName: '', Notes: '' };
const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

export function SoftwareEditForm() {
  const { id } = useParams();
  const poolId = id ? Number(id) : null;
  const isEdit = poolId !== null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const poolQuery = useQuery({ queryKey: ['software', poolId], queryFn: ({ signal }) => getSoftwarePool(poolId!, signal), enabled: isEdit });
  const form = useForm<SoftwareFormData>({ resolver: zodResolver(softwareSchema), defaultValues: empty });
  const { register, handleSubmit, reset, formState } = form;

  useEffect(() => {
    if (poolQuery.data) {
      const p = poolQuery.data.Pool;
      reset({ Title: p.Title, LicenceType: p.LicenceType, SKU: p.SKU ?? '', SkuId: p.SkuId ?? null, SecurityGroupId: p.SecurityGroupId ?? '', SecurityGroupName: p.SecurityGroupName ?? '', Notes: p.Notes ?? '' });
    }
  }, [poolQuery.data, reset]);

  function toPayload(d: SoftwareFormData): SoftwarePoolWrite {
    const blank = (v?: string) => (v && v.trim() !== '' ? v.trim() : undefined);
    return {
      Title: d.Title.trim(), LicenceType: d.LicenceType, SKU: blank(d.SKU), SkuId: d.SkuId ?? undefined, Notes: blank(d.Notes),
      // Always send the security-group fields (empty string clears an existing
      // link) so unlinking on edit actually persists.
      SecurityGroupId: d.SecurityGroupId?.trim() ?? '',
      SecurityGroupName: d.SecurityGroupName?.trim() ?? '',
    };
  }

  const mutation = useMutation({
    mutationFn: (d: SoftwareFormData) => (isEdit ? updateSoftwarePool(poolId!, toPayload(d)) : createSoftwarePool(toPayload(d))),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['software'] });
      navigate(`/software/${saved.SoftwarePoolId}`);
    },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not save the pool.'),
  });

  return (
    <section className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-navy">{isEdit ? 'Edit licence pool' : 'Add licence pool'}</h1>
      <p className="mt-1 text-sm text-darkGrey/70">Seats, cost per seat and vendor come from the contract line item that links to this pool.</p>
      {submitError && <div role="alert" className="mt-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{submitError}</div>}
      <form onSubmit={handleSubmit((d) => { setSubmitError(null); mutation.mutate(d); })} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-xs font-semibold text-darkGrey">Title<input {...register('Title')} className={inputCls} />{formState.errors.Title && <span className="mt-1 block font-normal text-red">{formState.errors.Title.message}</span>}</label>
          <label className="block text-xs font-semibold text-darkGrey">Licence type<select {...register('LicenceType')} className={inputCls}>{licenceTypes.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
          <label className="block text-xs font-semibold text-darkGrey">SKU (text)<input {...register('SKU')} className={inputCls} placeholder="Vendor / catalogue code (optional)" /></label>
          <label className="block text-xs font-semibold text-darkGrey">SKU (ID)<input type="number" {...register('SkuId', { setValueAs: (v) => (v === '' || v == null ? null : Number(v)) })} className={inputCls} placeholder="Numeric factor for integrations (optional)" />{formState.errors.SkuId && <span className="mt-1 block font-normal text-red">{formState.errors.SkuId.message}</span>}</label>
        </div>
        <fieldset className="rounded border border-border bg-lightBlue/40 p-3">
          <legend className="px-1 text-xs font-semibold text-navy">Azure security group</legend>
          <p className="mb-3 text-xs text-darkGrey/70">
            Link this pool to an Entra (Azure AD) security group so Azure can assign the licences. Paste the group's <strong>object ID</strong>; leave blank if the pool isn't provisioned through a group. The live member count and a suggested membership rule appear on the pool's Security group tab.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block text-xs font-semibold text-darkGrey">Group object ID<input {...register('SecurityGroupId')} className={inputCls} placeholder="00000000-0000-0000-0000-000000000000" />{formState.errors.SecurityGroupId && <span className="mt-1 block font-normal text-red">{formState.errors.SecurityGroupId.message}</span>}</label>
            <label className="block text-xs font-semibold text-darkGrey">Group name (optional)<input {...register('SecurityGroupName')} className={inputCls} placeholder="e.g. SG-Licence-BMA-Rental-Coordinator" /></label>
          </div>
        </fieldset>
        <label className="block text-xs font-semibold text-darkGrey">Notes<textarea {...register('Notes')} rows={3} className={inputCls} /></label>
        <div className="flex gap-2">
          <button type="submit" disabled={mutation.isPending} className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">{mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create pool'}</button>
          <button type="button" onClick={() => navigate(isEdit ? `/software/${poolId}` : '/software')} className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg">Cancel</button>
        </div>
      </form>
    </section>
  );
}
